'use client'

import React, { useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import { fetcher } from './utils/fetcher'
import { trimLongWords } from './utils/trimLongWords'
import { DropdownAddTag } from './DropdownAddTag'
import { LoadingSpinner } from './LoadingSpinner'
import type { Channel, ChannelsMap, DataStats, Pallete, PostsListProps, YoutubeData } from './types'
import { VideoCards } from './VideoCards'

interface AddProps {
  uniqueTags: string[]
  channelID: string
  apiUrl: string
  ch: Channel
  setChannelMap: React.Dispatch<React.SetStateAction<ChannelsMap>>
}

function Add({ uniqueTags, ch, apiUrl, channelID, setChannelMap }: AddProps) {
  return (
    <DropdownAddTag
      options={uniqueTags}
      value={ch?.tags || []}
      onSelect={async (selectedTags) => {
        const updatedChannel: Channel = { ...ch, tags: selectedTags }

        try {
          // Send the updated channel data to the /channels/upsert endpoint
          const response = await fetch(`${apiUrl}/channels/upsert`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ ...updatedChannel, uuid: channelID, tag_user_id: 1 }),
          })

          if (!response.ok) {
            throw new Error(`Failed to upsert channel: ${response.statusText}`)
          }

          const result = await response.json()
          setChannelMap((prev) => ({
            ...prev,
            [channelID]: { ...prev[channelID], tags: result.tags },
          }))
        } catch (e) {
          console.error('Error upserting channel:', e)
        }
      }}
      onDelete={async () => {
        try {
          console.log('Delete', { channelID })

          const response = await fetch(`${apiUrl}/channels/uuid/${channelID}`, {
            method: 'DELETE',
          })

          if (!response.ok) {
            throw new Error(`Failed to delete channel: ${response.statusText}`)
          }

          console.log('Channel successfully deleted:', channelID)

          // Update the local state to remove the deleted channel
          setChannelMap((prev) => {
            const updatedMap = { ...prev }
            delete updatedMap[channelID]
            return updatedMap
          })
        } catch (e) {
          console.error('Error deleting channel:', e)
        }
      }}
    />
  )
}

function YoutubeChannelsList({ apiUrl, title }: PostsListProps) {
  const [bCompact, setCompact] = useState(false)
  const [uniqueTags, setUniqueTags] = useState<string[]>([])
  const [tagCounter, setTagCounter] = useState<Record<string, number>>({})
  const [selTag, setSelTag] = useState('')
  const [channelMap, setChannelMap] = useState<ChannelsMap>({})

  const { data = {}, error } = useSWR<YoutubeData>(`${apiUrl}/youtube/channels`, fetcher)
  const { data: palletes = [], error: errorPalletes = {} } = useSWR<Pallete[]>(`${apiUrl}/palettes`, fetcher)
  const { data: channels = [], error: errorChannels = {} } = useSWR<Channel[]>(`${apiUrl}/channels`, fetcher)

  const [bStat, setStats] = useState(false)
  const { data: dataStats = {}, error: errorStats } = useSWR<DataStats>(
    bStat ? `${apiUrl}/youtube/stats` : null,
    fetcher
  )

  const { subscriptions = [], errors = [] } = data

  // Build channelMap and uniqueTags when channels changes
  useEffect(() => {
    if (!channels || channels.length == 0) return

    const newChannelMap: ChannelsMap = {}

    // TODO it's the way to add new tags and show them in priority
    const newUniqueTags = new Set<string>([
      'ai',
      'praat',
      'database',
      'leetcode',
      'cpp',
      'robotics',
      'visualization',
      'cv',
      'go',
      'python',
      'react',
      'swift',
      'k8s',
      'eth',
      'rust',
      'science',
      'lego',
      'security',
    ])
    const newTagCounter: Record<string, number> = {}

    channels.forEach((ch) => {
      newChannelMap[ch.uuid] = ch
      if (ch.tags) {
        ch.tags.forEach((t) => {
          newUniqueTags.add(t)
          if (!newTagCounter[t]) {
            newTagCounter[t] = 0
          }
          newTagCounter[t]++
        })
      }
    })

    setChannelMap(newChannelMap)
    setUniqueTags(Array.from(newUniqueTags))
    setTagCounter(newTagCounter)
    console.log('useEffect channelMap')
  }, [channels])

  // Build tagChannels: for the selected tag, which channels are included
  const tagChannels = useMemo(() => {
    if (!selTag || !channels) return {}
    const out: Record<string, string[]> = {}
    channels.forEach((c) => {
      if (c.tags && c.tags.includes(selTag)) {
        if (!out[selTag]) out[selTag] = []
        out[selTag].push(c.uuid)
      }
    })
    return out
  }, [selTag, channels])

  // Which subscriptions are "missing" in channelMap? (i.e., no matching channel.uuid)
  const missingSubs = useMemo(() => {
    return subscriptions.filter((sub) => {
      const channelID = sub.snippet?.resourceId?.channelId
      if (!channelID) return false
      const found = Object.values(channelMap).some((ch) => ch.uuid === channelID)
      return !found
    })
  }, [subscriptions, channelMap])

  console.log('XXX', {
    subscriptions,
    channelMap,
    missingSubs,
  })

  if (!subscriptions) {
    return (
      <div className="pt-4 text-sm">
        {error && error.message && <div>Error loading YoutubeChannelsList: {error.message}</div>}
        <LoadingSpinner /> ...
      </div>
    )
  }

  return (
    <>
      {/* TAG NAV */}
      <div className="p-1 text-sm">
        {error && error.message && <div>Error loading YoutubeChannelsList message:{error.message}</div>}
        {uniqueTags.map((tag, index) => {
          const suf = ` ${palletes[index % palletes.length]?.text_class} ${
            palletes[index % palletes.length]?.bg_class
          } `
          return (
            <span key={tag}>
              <a
                className={`whitespace-nowrap cursor-pointer pl-1 pr-1 rounded-xs text-xs rounded ${suf}`}
                onClick={(e) => {
                  e.preventDefault()
                  setSelTag(tag)
                }}
              >
                {tag} {tagCounter[tag] || 0}
              </a>{' '}
            </span>
          )
        })}{' '}
        {palletes && palletes[0] && palletes[0].text_class && (
          <>
            <span>
              <a
                className={`cursor-pointer pl-1 pr-1 rounded-xs text-xs rounded ${palletes[0].text_class} ${palletes[0].bg_class} `}
                onClick={(e) => {
                  e.preventDefault()
                  setSelTag('Empty')
                }}
              >
                Empty
              </a>{' '}
            </span>{' '}
            <span>
              {' '}
              <a
                className={`ml-1 cursor-pointer pl-1 pr-1 rounded-xs text-xs rounded ${palletes[0].text_class} ${palletes[0].bg_class} `}
                onClick={(e) => {
                  e.preventDefault()
                  setSelTag('')
                }}
              >
                SHOW ALL
              </a>{' '}
            </span>
          </>
        )}
      </div>

      <div className="text-sm">
        Compact
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault()
            setCompact(!bCompact)
          }}
          className="text-gray-600 pl-2"
        >
          {bCompact ? 'On' : 'Off'}
        </a>
      </div>

      {bCompact && (
        <>
          <div className="p-1 grid grid-cols-8 md:grid-cols-12 lg:grid-cols-24 gap-1">
            {subscriptions.map((post) => {
              const channelID = post.snippet?.resourceId?.channelId
              if (!channelID) return null

              // channelMap uses .uuid from /channels.
              // If "channelID" is not found in channelMap, skip or handle gracefully:
              const ch = Object.values(channelMap).find((c) => c.uuid === channelID)
              if (!ch) return null

              // filter by selected tag
              if (selTag && !ch.tags?.includes(selTag)) return null

              const parser = new DOMParser()
              const parsedTitle = parser.parseFromString(post.snippet.title, 'text/html').body.textContent
              const trimmedTitle = parsedTitle ? trimLongWords(parsedTitle, 30, 30) : ''

              return (
                <div key={post.id} className="bg-white shadow-sm p-0">
                  <a
                    href={`https://www.youtube.com/channel/${channelID}/videos`}
                    className="text-gray-800 hover:text-blue-800 hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      src={post.snippet.thumbnails.default.url}
                      className="object-cover h-4 w-full rounded-md"
                      style={{ width: 48, minHeight: 32 }}
                      alt={trimmedTitle}
                    />
                  </a>
                </div>
              )
            })}
          </div>
          <p>
            <strong>Unsubscribbed Channels </strong>
          </p>

          <div className="p-1 grid grid-cols-4 md:grid-cols-8 lg:grid-cols-24 gap-1">
            {channels.map((ch) => {
              if (selTag && !ch.tags?.includes(selTag)) return null

              // Filter out channels that are already subscribed
              const isSubscribed = subscriptions.some((post) => {
                const channelID = post.snippet?.resourceId?.channelId
                return channelID && channelID === ch.uuid
              })

              if (isSubscribed) return null

              const channelID = ch.uuid

              return (
                <div key={ch.uuid} className="bg-white rounded-md shadow-md p-1">
                  <div className="text font-semibold mt-1">
                    <a
                      href={ch.url}
                      className="text-gray-800 hover:text-blue-800 hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {ch.title}
                    </a>
                  </div>{' '}
                </div>
              )
            })}
          </div>
        </>
      )}

      {!bCompact && (
        <>
          <div className="p-1 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-1">
            {subscriptions.map((post) => {
              const channelID = post.snippet?.resourceId?.channelId
              if (!channelID) return null

              const ch = Object.values(channelMap).find((c) => c.uuid === channelID)
              if (!ch) return null

              if (selTag && !ch.tags?.includes(selTag)) return null

              const parser = new DOMParser()
              const parsedTitle = parser.parseFromString(post.snippet.title, 'text/html').body.textContent
              const trimmedTitle = parsedTitle ? trimLongWords(parsedTitle, 50, 50) : ''

              const parsedDesc = parser.parseFromString(post.snippet?.description, 'text/html').body.textContent
              const trimmedDesc = parsedDesc ? trimLongWords(parsedDesc, 50, 50) : ''

              return (
                <div key={post.id} className="bg-white rounded-md shadow-md p-1">
                  <a
                    href={`https://www.youtube.com/channel/${channelID}/videos`}
                    className="text-gray-800 hover:text-blue-800 hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src={post.snippet.thumbnails.default.url} className="object-cover h-12 w-full rounded-md" />
                  </a>
                  <div className="text font-semibold mt-1">
                    <a
                      href={`https://www.youtube.com/channel/${channelID}/videos`}
                      className="text-gray-800 hover:text-blue-800 hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {trimmedTitle}
                    </a>
                  </div>
                  {trimmedDesc && (
                    <p className="text-gray-700 mt-1 text-xs">
                      {trimmedDesc.length > 200 ? `${trimmedDesc.substring(0, 200)}...` : trimmedDesc}
                    </p>
                  )}
                  {ch.tags?.map((tag) => {
                    return (
                      <span key={tag}>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault()
                            setSelTag(tag)
                          }}
                        >
                          <span className="text-xs rounded-sm pl-1 pr-1 bg-blue-500 text-slate-50">{tag}</span>
                        </a>{' '}
                      </span>
                    )
                  })}{' '}
                  <Add
                    channelID={channelID}
                    ch={ch}
                    uniqueTags={uniqueTags}
                    setChannelMap={setChannelMap}
                    apiUrl={apiUrl}
                  />
                </div>
              )
            })}
          </div>

          <p>
            <strong>Unsubscribbed Channels </strong>
          </p>

          <div className="p-1 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-1">
            {channels.map((ch) => {
              if (selTag && !ch.tags?.includes(selTag)) return null

              // Filter out channels that are already subscribed
              const isSubscribed = subscriptions.some((post) => {
                const channelID = post.snippet?.resourceId?.channelId
                return channelID && channelID === ch.uuid
              })

              if (isSubscribed) return null

              const channelID = ch.uuid

              return (
                <div key={ch.uuid} className="bg-white rounded-md shadow-md p-1">
                  <div className="text font-semibold mt-1">
                    <a
                      href={ch.url}
                      className="text-gray-800 hover:text-blue-800 hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {ch.title}
                    </a>
                  </div>
                  {ch.short && (
                    <p className="text-gray-700 mt-1 text-xs">
                      {ch.short.length > 200 ? `${ch.short.substring(0, 200)}...` : ch.short}
                    </p>
                  )}
                  {ch.tags?.map((tag) => {
                    return (
                      <span key={tag}>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault()
                            setSelTag(tag)
                          }}
                        >
                          <span className="text-xs rounded-sm pl-1 pr-1 bg-blue-500 text-slate-50">{tag}</span>
                        </a>{' '}
                      </span>
                    )
                  })}{' '}
                  <Add
                    channelID={channelID}
                    ch={ch}
                    uniqueTags={uniqueTags}
                    setChannelMap={setChannelMap}
                    apiUrl={apiUrl}
                  />
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* Show videos for selected tag */}
      {tagChannels[selTag] && <VideoCards ids={tagChannels[selTag]} apiUrl={apiUrl} />}

      {/* Show missing channels, if any */}
      {missingSubs.length > 0 && (
        <div className="mt-2 text-red-600 text-sm">
          <strong>No Tags Channels ({missingSubs.length})</strong>
          <div className="p-1 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-1">
            {missingSubs.map((post) => {
              const channelID = post.snippet?.resourceId?.channelId
              const img = post.snippet?.thumbnails?.default?.url

              const parser = new DOMParser()
              const parsedTitle = parser.parseFromString(post.snippet.title, 'text/html').body.textContent
              const trimmedTitle = parsedTitle ? trimLongWords(parsedTitle, 50, 50) : ''

              const parsedDesc = parser.parseFromString(post.snippet?.description, 'text/html').body.textContent
              const trimmedDesc = parsedDesc ? trimLongWords(parsedDesc, 50, 50) : ''

              const ch: Channel = {
                uuid: channelID,
                title: trimmedTitle,
                short: trimmedDesc,
                url: `https://www.youtube.com/channel/${channelID}/videos`,
                tags: [],
                img,
              }

              return (
                <div key={post.id} className="bg-white rounded-md shadow-md p-1">
                  <a
                    href={ch.url}
                    className="text-gray-800 hover:text-blue-800 hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src={post.snippet.thumbnails.default.url} className="object-cover h-12 w-full rounded-md" />
                  </a>
                  <div className="text font-semibold mt-1">
                    <a
                      href={`https://www.youtube.com/channel/${channelID}/videos`}
                      className="text-gray-800 hover:text-blue-800 hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {trimmedTitle}
                    </a>
                  </div>
                  {trimmedDesc && (
                    <p className="text-gray-700 mt-1 text-xs">
                      {trimmedDesc.length > 200 ? `${trimmedDesc.substring(0, 200)}...` : trimmedDesc}
                    </p>
                  )}{' '}
                  <Add
                    channelID={channelID}
                    ch={ch}
                    uniqueTags={uniqueTags}
                    setChannelMap={setChannelMap}
                    apiUrl={apiUrl}
                  />
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Stats / errors */}
      <div className="mt-4 mb-1">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault()
            setStats(true)
          }}
          className="text-gray-500 text-sm"
        >
          Show stats / errors ({errors && Array.isArray(errors) ? errors.length : 0})
        </a>
        {bStat && <div className="text-gray-500 text-sm">{JSON.stringify({ dataStats, errors })}</div>}

        {/* for tailwind generation */}
        <div className="text-slate-50 bg-gray-500 text-slate-50 bg-red-500 bg-yellow-500  bg-green-500  bg-blue-500  bg-indigo-500  bg-purple-500  bg-pink-500  bg-gray-600  bg-red-600  bg-yellow-600  bg-green-600  bg-blue-600  bg-indigo-600  bg-purple-600  bg-pink-600  bg-gray-700  bg-red-700  bg-yellow-700  bg-green-700 bg-gray-500 w-[1px] h-[1px] m-0 p-0 right-0 left-0">
          .
        </div>
      </div>
    </>
  )
}

export default YoutubeChannelsList
